import { inject, Injectable, OnInit } from '@angular/core';
import { forkJoin, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { CustomerService } from '@services/customer.service';
import { ServiceRequestTypeEnum } from '@enums/service-request-type.enum';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { CustomerOnboardingHttpService } from './customer-onboarding-http.service';

/**
 * Holds shared customer onboarding session state loaded once per customer.
 */
@Injectable()
export class CustomerOnboardingContextService {
  private readonly customerService = inject(CustomerService);

  private readonly customerOnboardingHttpService = inject(CustomerOnboardingHttpService);

  private readonly customerIdSubject: ReplaySubject<string> = new ReplaySubject<string>(1);

  private currentCustomerId = '';

  private currentCustomerName = '';

  private customerSRTypes: ServiceRequestTypeEnum[] = [];

  private readonly customerId$: Observable<string> = this.customerIdSubject.asObservable().pipe(distinctUntilChanged());

  private readonly customer$: Observable<BaseDropdownInterface> = this.customerId$.pipe(
    switchMap((customerId: string): Observable<BaseDropdownInterface> => this.customerService.getCustomerName(customerId)),
    tap((customer: BaseDropdownInterface): void => {
      this.currentCustomerName = customer?.name ?? '';
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly customerSRTypes$ = this.customerId$.pipe(
    switchMap(
      (customerId: string): Observable<ServiceRequestTypeEnum[]> => this.customerOnboardingHttpService.getCustomerSRTypes(customerId),
    ),
    tap((customerSRTypes: ServiceRequestTypeEnum[]): void => {
      this.setSelectedSRTypes(customerSRTypes);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Sets current customer identifier for onboarding context stream.
   *
   * @param customerId - customer identifier from route params
   */
  initialize(customerId: string): void {
    this.currentCustomerId = customerId;

    this.customerIdSubject.next(customerId);
  }

  /**
   * Loads onboarding context once and completes.
   * Used by route resolvers to ensure customer context is available before activation.
   *
   * @param customerId - customer identifier from route params
   */
  ensureLoaded$(customerId: string): Observable<unknown> {
    this.initialize(customerId);

    // return type is Observable<unknown> because the value shouldn't be used from here
    return forkJoin({
      customer: this.customer$.pipe(take(1)),
      customerSrTypes: this.customerSRTypes$.pipe(take(1)),
    });
  }

  /**
   * Updates cached selected SR types (e.g. after saving service options).
   *
   * @param selectedSrTypes - selected service request type names
   */
  setSelectedSRTypes(selectedSrTypes: ServiceRequestTypeEnum[]): void {
    this.customerSRTypes = [...selectedSrTypes];
  }

  /**
   * Current onboarding customer identifier.
   */
  get customerId(): string {
    return this.currentCustomerId;
  }

  /**
   * Current onboarding customer name.
   */
  get customerName(): string {
    return this.currentCustomerName;
  }

  /**
   * Selected service request types loaded for the current customer.
   */
  get selectedSRTypes(): ServiceRequestTypeEnum[] {
    return this.customerSRTypes;
  }
}
